import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { CONFIG } from "../config";
import posthog from "posthog-js";

const StatusStackedBarGraph = ({ selectedDate, selectedStatus, onStatusChange }) => {
  const chartRef = useRef();
  const containerRef = useRef();
  const [statusData, setStatusData] = useState([]);
  const [totalVehicles, setTotalVehicles] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chartWidth, setChartWidth] = useState(452);

  const validStatuses = ["healthy", "at_risk", "down"];
  const isValidStatus = validStatuses.includes(selectedStatus);
  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const restrictedDevice = process.env.REACT_APP_RESTRICTED_DEVICE || '';
const deviceProvider = localStorage.getItem("tensor-user-device-provider");
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  //const isRestricted = deviceProvider === restrictedDevice;
const isRestricted = deviceProvider === restrictedDevice;

  const MIN_SEGMENT_WIDTH = 30;

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setChartWidth(containerRef.current.offsetWidth - 40);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchStatusData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.STATUS_STACKED_BAR_GRAPH_URL}/${localStorage.getItem("tensor-user-id")}?date_str=${selectedDate}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );
        const data = await response.json();
        if (data.success) {
          setTotalVehicles(data.total);
          setStatusData([
            {
              label: "Healthy",
              value: data.healthy,
              status_label: "healthy",
              color: "#30A46C",
              text_color: "#46A758",
              icon: "ti-circle-check",
            },
            {
              label: "At Risk",
              value: data.at_risk,
              status_label: "at_risk",
              color: "#FFC53D",
              text_color: "#915930",
              icon: "ti-alert-triangle",
            },
            {
              label: "Down",
              value: data.down,
              status_label: "down",
              color: "#E5484D",
              text_color: "#E5484D",
              icon: "ti-circle-x",
            },
          ]);
        } else {
          setStatusData([]);
          setTotalVehicles(null);
        }
      } catch (error) {
        console.error("Failed to fetch status data:", error);
        setStatusData([]);
        setTotalVehicles(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatusData();
  }, [selectedDate]);

  useEffect(() => {
    if (statusData.length === 0 || !chartWidth) return;

    const margin = { top: 30, right: 20, bottom: -10, left: 20 };
    const height = 22;

    d3.select(chartRef.current).selectAll("*").remove();

    const svg = d3
      .select(chartRef.current)
      .append("svg")
      .attr("width", chartWidth + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const totalAvailableWidth = chartWidth;
    const zeroValueCount = statusData.filter((d) => d.value === 0).length;
    const reservedWidth = zeroValueCount * MIN_SEGMENT_WIDTH;
    const remainingWidth = totalAvailableWidth - reservedWidth;

    const totalNonZero = d3.sum(statusData, (d) => (d.value > 0 ? d.value : 0));
    const adjustedScale = d3
      .scaleLinear()
      .domain([0, totalNonZero || 1])
      .range([0, remainingWidth]);

    let xOffset = 0;

    statusData.forEach((segment) => {
      const isZero = segment.value === 0;
      const segmentWidth = isZero ? MIN_SEGMENT_WIDTH : adjustedScale(segment.value);

      const handleClick = () => {
        if (onStatusChange) {
          onStatusChange(segment.status_label);
          posthog.capture("vehicles_status_filter_applied", {
            status: segment.status_label,
            label: segment.label,
            value: segment.value,
            totalVehicles: totalVehicles || 0,
          });
        }
      };

      g.append("rect")
        .attr("x", xOffset)
        .attr("y", -10)
        .attr("width", segmentWidth)
        .attr("height", height)
        .attr("fill", segment.color)
        .style("cursor", "pointer")
        .style("opacity", () => {
          if (!isValidStatus) return 1;
          if (isZero) return 0.6;
          return selectedStatus === segment.status_label ? 1 : 0.4;
        })
        .on("click", handleClick);

      g.append("text")
        .attr("x", xOffset + segmentWidth / 2)
        .attr("y", height / 2 - 8)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("fill", "#FDF8F8")
        .style("font-weight", "400")
        .style("font-size", "14px")
        .text(segment.value)
        .style("cursor", "pointer")
        .on("click", handleClick);

      xOffset += segmentWidth;
    });
  }, [statusData, chartWidth, selectedStatus]);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      {isLoading ? (
        <div>Loading status data...</div>
      ) : statusData.length === 0 ? (
        <div>No status data available.</div>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-600 mb-1 text-left total_vehicles_text">
            {totalVehicles ? totalVehicles.toLocaleString() : "-"}
          </p>
          <div ref={chartRef}></div>
          <div
            className="label-icons"
            style={{
              position: "absolute",
              top: "35px",
              left: "20px",
              width: `${chartWidth}px`,
              height: "0px",
              pointerEvents: "none",
            }}
          >
            {(() => {
              const totalAvailableWidth = chartWidth;
              const zeroValueCount = statusData.filter((d) => d.value === 0).length;
              const reservedWidth = zeroValueCount * MIN_SEGMENT_WIDTH;
              const remainingWidth = totalAvailableWidth - reservedWidth;

              const totalNonZero = d3.sum(statusData, (d) => (d.value > 0 ? d.value : 0));
              const adjustedScale = d3
                .scaleLinear()
                .domain([0, totalNonZero || 1])
                .range([0, remainingWidth]);

              return statusData.map((segment, index) => {
                const isZero = segment.value === 0;
                const segmentWidth = isZero ? MIN_SEGMENT_WIDTH : adjustedScale(segment.value);

                const center =
                  statusData
                    .slice(0, index)
                    .reduce(
                      (acc, curr) =>
                        acc +
                        (curr.value === 0 ? MIN_SEGMENT_WIDTH : adjustedScale(curr.value)),
                      0
                    ) +
                  segmentWidth / 2;

                const isSelected =
                  isValidStatus && selectedStatus === segment.status_label;

                return (
                  <div
                    key={index}
                    style={{
                      position: "absolute",
                      left: `${center - 40}px`,
                      top: "0px",
                      fontSize: "14px",
                      fontWeight: 400,
                      display: "flex",
                      alignItems: "center",
                      color: segment.text_color,
                      gap: "4px",
                      width: "80px",
                      justifyContent: "center",
                      lineHeight: 1,
                      opacity: !isValidStatus ? 1 : isSelected ? 1 : 0.4,
                      transition: "opacity 0.3s ease-in-out",
                    }}
                  >
                    <i
                      className={`ti ${segment.icon}`}
                      style={{
                        fontSize: "16px",
                        transform: "translateY(-1px)",
                        display: "inline-block",
                      }}
                    ></i>
                    <span style={{ position: "relative", top: "-1px" }}>
                      {segment.label}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default StatusStackedBarGraph;
